import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { apiClient } from '../../../services/api';
import { useToast } from '../../../contexts/ToastContext';
import { TenantDetail, TenantOrganizationDetail, Plan } from '../../../types/domain';

export function useTenantDetail(id?: string) {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { showToast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [organizations, setOrganizations] = useState<TenantOrganizationDetail[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);

  // Profile Form State
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Password Reset State
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [generatedResetUrl, setGeneratedResetUrl] = useState<string | null>(null);

  // Edit Org Quotas State
  const [editingOrg, setEditingOrg] = useState<TenantOrganizationDetail | null>(null);
  const [editTier, setEditTier] = useState('free');
  const [editMaxSubmissions, setEditMaxSubmissions] = useState(500);
  const [editMaxTables, setEditMaxTables] = useState(3);
  const [editMaxSeats, setEditMaxSeats] = useState(2);
  const [isUpdatingOrg, setIsUpdatingOrg] = useState(false);

  // Add Org State
  const [addOrgModalOpen, setAddOrgModalOpen] = useState(false);
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [orgSearchResults, setOrgSearchResults] = useState<Array<{ id: string; name: string; slug: string; plan_tier?: string; tables_count?: number }>>([]);
  const [selectedOrg, setSelectedOrg] = useState<{ id: string; name: string; slug: string; plan_tier?: string } | null>(null);
  const [selectedRole, setSelectedRole] = useState<'member' | 'admin' | 'owner'>('member');
  const [isSearchingOrgs, setIsSearchingOrgs] = useState(false);
  const [isAddingOrg, setIsAddingOrg] = useState(false);

  // Delete User State
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Superadmin Guard
  useEffect(() => {
    if (currentUser && !currentUser.is_superadmin) {
      showToast({
        type: 'error',
        title: 'Unauthorized Access',
        message: 'Platform administrator permissions are required.',
      });
      navigate('/dashboard', { replace: true });
    }
  }, [currentUser, navigate, showToast]);

  const fetchTenantData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const [tenantRes, orgsRes, plansRes] = await Promise.all([
        apiClient<{ tenant: TenantDetail }>(`/admin/tenants/${id}`),
        apiClient<{ data: TenantOrganizationDetail[] }>(`/admin/tenants/${id}/organizations`),
        apiClient<{ data: Plan[] }>('/plans').catch(() => ({ data: [] })),
      ]);

      const t = tenantRes.tenant;
      setTenant(t);
      setFullName(t.full_name || '');
      setEmail(t.email || '');
      setIsActive(t.is_active);
      setIsSuperadmin(t.is_superadmin);
      setOrganizations(orgsRes.data || []);
      setPlans(plansRes.data || []);
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Failed to load user',
        message: err?.message || 'Could not retrieve user details',
      });
      navigate('/admin/tenants', { replace: true });
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate, showToast]);

  useEffect(() => {
    fetchTenantData();
  }, [fetchTenantData]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    if (!fullName.trim()) {
      showToast({ type: 'error', title: 'Validation Error', message: 'Full name is required' });
      return;
    }

    setIsSaving(true);
    try {
      const res = await apiClient<{ tenant: TenantDetail }>(`/admin/tenants/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          full_name: fullName.trim(),
          email: email.trim(),
          is_active: isActive,
          is_superadmin: isSuperadmin,
        }),
      });

      setTenant(res.tenant);
      showToast({
        type: 'success',
        title: 'Profile Updated',
        message: 'User details saved successfully.',
      });
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Update Failed',
        message: err?.message || 'Could not update user profile',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setIsResettingPassword(true);
    try {
      const payload = newPassword.trim()
        ? { password: newPassword.trim() }
        : { generate_link: true };

      const res = await apiClient<{ direct_updated: boolean; reset_url?: string; message: string }>(
        `/admin/tenants/${id}/reset-password`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      );

      if (res.direct_updated) {
        showToast({
          type: 'success',
          title: 'Password Updated',
          message: res.message,
        });
        setResetModalOpen(false);
        setNewPassword('');
      } else if (res.reset_url) {
        const fullUrl = `${window.location.origin}${res.reset_url}`;
        setGeneratedResetUrl(fullUrl);
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Password Reset Failed',
        message: err?.message || 'Could not process password reset',
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleUpdateOrgQuotas = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !editingOrg) return;

    setIsUpdatingOrg(true);
    try {
      await apiClient(`/admin/tenants/${id}/organizations/${editingOrg.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          plan_tier: editTier,
          max_monthly_submissions: editMaxSubmissions,
          max_tables: editMaxTables,
          max_team_seats: editMaxSeats,
        }),
      });

      showToast({
        type: 'success',
        title: 'Organization Updated',
        message: 'Subscription tier and quotas updated successfully.',
      });
      setEditingOrg(null);
      fetchTenantData();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Update Failed',
        message: err?.message || 'Could not update organization settings',
      });
    } finally {
      setIsUpdatingOrg(false);
    }
  };

  const handleRemoveFromOrg = async (org: TenantOrganizationDetail) => {
    if (!id) return;
    if (!window.confirm(`Are you sure you want to remove this user from "${org.name}"?`)) return;

    try {
      await apiClient(`/admin/tenants/${id}/organizations/${org.id}`, {
        method: 'DELETE',
      });

      showToast({
        type: 'success',
        title: 'Membership Removed',
        message: `Removed from ${org.name}.`,
      });
      fetchTenantData();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Removal Failed',
        message: err?.message || 'Could not remove member from organization',
      });
    }
  };

  const handleSearchOrgs = async (query: string) => {
    setOrgSearchQuery(query);
    if (!query.trim()) {
      setOrgSearchResults([]);
      return;
    }

    setIsSearchingOrgs(true);
    try {
      const res = await apiClient<{ data: any[] }>(`/admin/organizations?search=${encodeURIComponent(query)}&limit=8`);
      setOrgSearchResults(res.data || []);
    } catch (err) {
      setOrgSearchResults([]);
    } finally {
      setIsSearchingOrgs(false);
    }
  };

  const handleAssignToOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !selectedOrg) return;

    setIsAddingOrg(true);
    try {
      await apiClient(`/admin/tenants/${id}/organizations`, {
        method: 'POST',
        body: JSON.stringify({
          organization_id: selectedOrg.id,
          role: selectedRole,
        }),
      });

      showToast({
        type: 'success',
        title: 'Added to Organization',
        message: `User has been assigned to ${selectedOrg.name} as ${selectedRole}.`,
      });
      setAddOrgModalOpen(false);
      setSelectedOrg(null);
      setOrgSearchQuery('');
      setOrgSearchResults([]);
      fetchTenantData();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Assignment Failed',
        message: err?.message || 'Could not assign user to organization',
      });
    } finally {
      setIsAddingOrg(false);
    }
  };

  const handleDeleteTenant = async () => {
    if (!id) return;
    setIsDeletingUser(true);
    try {
      await apiClient(`/admin/tenants/${id}`, {
        method: 'DELETE',
      });

      showToast({
        type: 'success',
        title: 'Tenant Deleted',
        message: 'Tenant account permanently deleted.',
      });
      navigate('/admin/tenants', { replace: true });
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Deletion Failed',
        message: err?.message || 'Could not delete tenant account',
      });
    } finally {
      setIsDeletingUser(false);
      setShowDeleteConfirm(false);
    }
  };

  return {
    isLoading,
    tenant,
    organizations,
    plans,
    currentUser,
    isSelf: currentUser?.id === tenant?.id,
    fullName,
    setFullName,
    email,
    setEmail,
    isActive,
    setIsActive,
    isSuperadmin,
    setIsSuperadmin,
    isSaving,
    handleSaveProfile,
    resetModalOpen,
    setResetModalOpen,
    newPassword,
    setNewPassword,
    isResettingPassword,
    generatedResetUrl,
    setGeneratedResetUrl,
    handleResetPassword,
    editingOrg,
    setEditingOrg,
    editTier,
    setEditTier,
    editMaxSubmissions,
    setEditMaxSubmissions,
    editMaxTables,
    setEditMaxTables,
    editMaxSeats,
    setEditMaxSeats,
    isUpdatingOrg,
    handleUpdateOrgQuotas,
    handleRemoveFromOrg,
    addOrgModalOpen,
    setAddOrgModalOpen,
    orgSearchQuery,
    setOrgSearchQuery,
    orgSearchResults,
    selectedOrg,
    setSelectedOrg,
    selectedRole,
    setSelectedRole,
    isSearchingOrgs,
    isAddingOrg,
    handleSearchOrgs,
    handleAssignToOrg,
    showDeleteConfirm,
    setShowDeleteConfirm,
    isDeletingUser,
    handleDeleteTenant,
  };
}
